import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { formatDate, trFold, SearchClear } from "@/components/shared";
import { MailPlus, Send, Pencil, Trash2, CheckCircle2, Search } from "lucide-react";
import { toast } from "sonner";

const TYPES = ["Stand Üreticisi", "Mobilya Üreticisi", "Dekor & Tasarım", "Etkinlik Alanı", "Sahne & Prodüksiyon"];
const statusMeta = {
  pending: { label: "Davet Edildi · Bekliyor", cls: "bg-yellow-500/10 text-yellow-600 border-yellow-500/20" },
  accepted: { label: "Kabul Etti · Onay Bekliyor", cls: "bg-blue-500/10 text-blue-600 border-blue-500/20" },
  approved: { label: "Onaylandı · Havuzda", cls: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20" },
};
const emptyForm = { company_name: "", contact_name: "", email: "", account_type: "" };

export default function InvitesPage() {
  const [invites, setInvites] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [rowBusy, setRowBusy] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [q, setQ] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const load = () => api.get("/invites").then((r) => setInvites(r.data)).catch(() => setInvites([]));
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (inv) => { setEditing(inv); setForm({ company_name: inv.company_name, contact_name: inv.contact_name, email: inv.email, account_type: inv.account_type }); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editing) {
        await api.patch(`/invites/${editing.id}`, form);
        toast.success("Davet güncellendi");
      } else {
        await api.post("/invites", { ...form, account_type: form.account_type || "Stand Üreticisi" });
        toast.success("Davet e-postası gönderildi");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const resend = async (id) => {
    setRowBusy(id);
    try {
      await api.post(`/invites/${id}/resend`);
      toast.success("Davet e-postası yeniden gönderildi");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setRowBusy(null);
    }
  };

  const approve = async (id) => {
    setRowBusy(id);
    try {
      await api.post(`/invites/${id}/approve`);
      toast.success("Tedarikçi havuza alındı — tebrik e-postası gönderildi");
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setRowBusy(null);
    }
  };

  const remove = async (id) => {
    if (confirmDel !== id) { setConfirmDel(id); return; }
    setRowBusy(id);
    try {
      await api.delete(`/invites/${id}`);
      toast.success("Davet silindi");
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setRowBusy(null);
      setConfirmDel(null);
    }
  };

  if (!invites) return <p className="text-slate-500 text-sm">Yükleniyor...</p>;

  const matches = (i) =>
    (statusFilter === "all" || i.status === statusFilter) &&
    (!q.trim() || trFold([i.company_name, i.contact_name, i.email].join(" ")).includes(trFold(q.trim())));

  return (
    <div className="space-y-8 fade-in" data-testid="invites-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Yönetim</div>
          <h1 className="font-heading text-4xl font-extrabold tracking-tighter mt-1">Tedarikçi Davetleri</h1>
          <p className="text-sm text-slate-500 mt-2">COP31 Antalya tedarikçi havuzuna davet gönderin. Davet bağlantıları süresizdir.</p>
        </div>
        <Button onClick={openCreate} data-testid="new-invite-button" className="rounded-none bg-slate-900 text-white hover:bg-slate-700 gap-2 font-semibold transition-colors duration-200">
          <MailPlus size={15} /> Davet Gönder
        </Button>
      </div>

      {invites.length === 0 ? (
        <p className="text-slate-500 text-sm border border-slate-200 bg-white p-6" data-testid="no-invites">Henüz davet gönderilmedi.</p>
      ) : (
        <>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex flex-wrap gap-1" data-testid="invite-status-filter">
          {[
            { key: "all", label: "Tümü" },
            { key: "pending", label: "Bekliyor" },
            { key: "accepted", label: "Onay Bekliyor" },
            { key: "approved", label: "Havuzda" },
          ].map((f) => {
            const count = f.key === "all" ? invites.length : invites.filter((i) => i.status === f.key).length;
            return (
              <button
                key={f.key}
                onClick={() => setStatusFilter(f.key)}
                data-testid={`invite-filter-${f.key}`}
                className={`px-4 py-2 text-sm transition-colors duration-200 ${
                  statusFilter === f.key ? "bg-slate-900 text-white font-semibold" : "bg-white border border-slate-200 text-slate-500 hover:text-slate-900"
                }`}
              >
                {f.label} <span className={`text-xs ml-1 ${statusFilter === f.key ? "text-slate-400" : "text-slate-400"}`}>({count})</span>
              </button>
            );
          })}
          </div>
          <div className="relative flex-1 min-w-[220px] max-w-sm">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Firma, yetkili veya e-posta ara..." data-testid="invite-search-input" className="rounded-none bg-white border-slate-200 h-[38px] pl-9 pr-9" />
            <SearchClear value={q} onClear={() => setQ("")} testid="invite-search-clear" />
          </div>
        </div>
        {invites.filter(matches).length === 0 ? (
          <p className="text-slate-500 text-sm border border-slate-200 bg-white p-6" data-testid="no-filtered-invites">Aramanızla eşleşen davet bulunmuyor.</p>
        ) : (
        <div className="border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-sm min-w-[860px]">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-5 py-3 font-medium">Firma</th>
                <th className="px-5 py-3 font-medium">Yetkili</th>
                <th className="px-5 py-3 font-medium">E-posta</th>
                <th className="px-5 py-3 font-medium">Durum</th>
                <th className="px-5 py-3 font-medium">Tarih</th>
                <th className="px-5 py-3 font-medium text-right">İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {invites.filter(matches).map((inv) => (
                <tr key={inv.id} className="border-b border-slate-100 last:border-0" data-testid={`invite-row-${inv.id}`}>
                  <td className="px-5 py-4 font-semibold text-slate-900">{inv.company_name}</td>
                  <td className="px-5 py-4 text-slate-600">{inv.contact_name}</td>
                  <td className="px-5 py-4 text-slate-600">{inv.email}</td>
                  <td className="px-5 py-4">
                    <Badge variant="outline" className={`rounded-none text-[10px] ${statusMeta[inv.status]?.cls}`} data-testid={`invite-status-${inv.id}`}>
                      {statusMeta[inv.status]?.label || inv.status}
                    </Badge>
                  </td>
                  <td className="px-5 py-4 text-slate-500 whitespace-nowrap">{formatDate(inv.created_at)}</td>
                  <td className="px-5 py-4">
                    <div className="flex items-center justify-end gap-2 flex-wrap">
                      {inv.status === "accepted" && (
                        <Button size="sm" onClick={() => approve(inv.id)} disabled={rowBusy === inv.id} data-testid={`approve-invite-${inv.id}`} className="rounded-none bg-emerald-600 hover:bg-emerald-500 text-white gap-1.5 h-8 font-semibold transition-colors duration-200">
                          <CheckCircle2 size={12} /> Onayla
                        </Button>
                      )}
                      {inv.status !== "approved" && (
                        <>
                          <Button size="sm" variant="outline" onClick={() => resend(inv.id)} disabled={rowBusy === inv.id} data-testid={`resend-invite-${inv.id}`} className="rounded-none border-slate-200 hover:bg-slate-100 gap-1.5 h-8 transition-colors duration-200">
                            <Send size={12} /> Yeniden Gönder
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => openEdit(inv)} data-testid={`edit-invite-${inv.id}`} className="rounded-none border-slate-200 hover:bg-slate-100 gap-1.5 h-8 transition-colors duration-200">
                            <Pencil size={12} /> Düzenle
                          </Button>
                        </>
                      )}
                      <Button size="sm" variant="outline" onClick={() => remove(inv.id)} disabled={rowBusy === inv.id} data-testid={`delete-invite-${inv.id}`} className={`rounded-none gap-1.5 h-8 transition-colors duration-200 ${confirmDel === inv.id ? "bg-red-600 text-white border-red-600 hover:bg-red-700 hover:text-white" : "border-slate-200 text-slate-500 hover:bg-slate-100"}`}>
                        <Trash2 size={12} /> {confirmDel === inv.id ? "Emin misiniz?" : "Sil"}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
        </>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent aria-describedby={undefined} className="bg-white border-slate-200 rounded-none sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-bold tracking-tight">{editing ? "Daveti Düzenle" : "Tedarikçi Davet Et"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label>Firma Adı</Label>
              <Input value={form.company_name} onChange={set("company_name")} required data-testid="invite-company-input" className="rounded-none bg-white border-slate-200 h-11" />
            </div>
            <div className="space-y-1.5">
              <Label>Yetkili Adı Soyadı</Label>
              <Input value={form.contact_name} onChange={set("contact_name")} required data-testid="invite-contact-input" className="rounded-none bg-white border-slate-200 h-11" />
            </div>
            <div className="space-y-1.5">
              <Label>E-posta</Label>
              <Input type="email" value={form.email} onChange={set("email")} required data-testid="invite-email-input" className="rounded-none bg-white border-slate-200 h-11" />
            </div>
            <div className="space-y-1.5">
              <Label>Hesap Tipi</Label>
              <Select value={form.account_type} onValueChange={(v) => { if (v) setForm((f) => ({ ...f, account_type: v })); }}>
                <SelectTrigger className="rounded-none bg-white border-slate-200 h-11" data-testid="invite-type-select">
                  <SelectValue placeholder="Seçin (varsayılan: Stand Üreticisi)" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200 rounded-none">
                  {TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" disabled={busy} data-testid="invite-submit-button" className="w-full rounded-none h-11 bg-slate-900 text-white hover:bg-slate-700 font-semibold transition-colors duration-200">
              {busy ? "Gönderiliyor..." : editing ? "Kaydet" : "Daveti Gönder"}
            </Button>
            {!editing && <p className="text-xs text-slate-500">Davet e-postası COP31 Antalya markasıyla gönderilir; bağlantı süresiz geçerlidir.</p>}
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
